<template>
  <div class="pl-payroll-page">
    <div v-if="loading" class="text-center p-lg">
      <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
    </div>
    <!-- The what-is-owed explainer lives in the page header (coach_pay/index),
         collapsed behind a details toggle -->
    <template v-else>
      <div v-if="rows.length === 0" class="bg-gray-50 rounded-sm p-md text-center">
        <p class="text-gray-600 m-b-none">No coaches accepting private lessons yet.</p>
      </div>

      <template v-else>
        <!-- One-glance size of this payroll run -->
        <div class="pl-pay-stats flex">
          <div class="pl-pay-stat">
            <p class="text-20 font-heavy m-b-none pl-slot-time">{{ dollars(totalOwed) }}</p>
            <p class="text-12 text-gray-600 m-b-none">Owed right now</p>
          </div>
          <div class="pl-pay-stat">
            <p class="text-20 font-heavy m-b-none pl-slot-time">{{ needsPayment.length }}</p>
            <p class="text-12 text-gray-600 m-b-none">{{ needsPayment.length === 1 ? 'Coach' : 'Coaches' }} to pay</p>
          </div>
          <div class="pl-pay-stat">
            <p class="text-20 font-heavy m-b-none pl-slot-time" :class="{ 'text-yellow-700': uncollectedCount > 0 }">{{ uncollectedCount }}</p>
            <p class="text-12 text-gray-600 m-b-none">Awaiting collection</p>
          </div>
          <div v-if="refundedCount > 0" class="pl-pay-stat">
            <p class="text-20 font-heavy m-b-none pl-slot-time text-red-600">{{ refundedCount }}</p>
            <p class="text-12 text-gray-600 m-b-none">{{ refundedCount === 1 ? 'Lesson' : 'Lessons' }} refunded</p>
          </div>
        </div>

        <template v-if="needsPayment.length > 0">
          <p class="pl-pay-section-label m-t-md m-b-none">Needs payment</p>
          <payroll-coach-row
            v-for="row in needsPayment"
            :key="row.coach_id"
            :row="row"
            :generated-at="generatedAt"
            @refresh="fetchSummary">
          </payroll-coach-row>
        </template>
        <div v-else class="bg-gray-50 rounded-sm p-md text-center m-t-md">
          <p class="text-gray-600 m-b-none">All coaches are settled up.</p>
        </div>

        <template v-if="settled.length > 0">
          <p class="m-t-md m-b-none">
            <a class="text-14 cursor-pointer text-gray-600" data-cy="toggle-settled" @click="showSettled = !showSettled">
              <i :class="showSettled ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
              {{ showSettled ? 'Hide' : 'Show' }} {{ settled.length }} settled {{ settled.length === 1 ? 'coach' : 'coaches' }}
            </a>
          </p>
          <template v-if="showSettled">
            <payroll-coach-row
              v-for="row in settled"
              :key="row.coach_id"
              :row="row"
              :generated-at="generatedAt"
              @refresh="fetchSummary">
            </payroll-coach-row>
          </template>
        </template>
      </template>
    </template>

    <div v-show="error" class="m-t-sm">
      <small><span class="text-danger">{{ errorMessage }}</span></small>
    </div>
  </div>
</template>

<script>
import PayrollCoachRow from './payroll_coach_row.vue';
import { errorableMix } from './mixins/errorable_mix';
import { currencyMix } from '../../shared/currency_mix';

// Manual payroll for private lessons: per-coach owed-since-last-payout with a
// mark-as-paid ledger (CoachPayout). Money math is Poro::PrivateLessonPayroll
// server-side — the same calculation behind the CSV report and the coach
// earnings card, and eventually behind real processor payouts.
//
// This page is the run-through-everyone view: a summary strip, then coaches
// who need paying sorted by amount, with settled coaches collapsed out of the
// way. Row mechanics live in payroll-coach-row.
//
// Private lessons are the only payroll source today, hence the name; the page
// itself is generic enough that a second source should slot in beside them.
export default {
  name: 'private-lesson-payroll-page',
  components: { PayrollCoachRow },
  mixins: [errorableMix, currencyMix],
  data() {
    return {
      loading: true,
      rows: [],
      generatedAt: null,
      showSettled: false,
    };
  },
  computed: {
    needsPayment() {
      return this.rows
        .filter((row) => row.owed.coach_pay > 0)
        .sort((a, b) => b.owed.coach_pay - a.owed.coach_pay);
    },
    settled() {
      return this.rows.filter((row) => row.owed.coach_pay <= 0);
    },
    totalOwed() {
      return this.needsPayment.reduce((sum, row) => sum + row.owed.coach_pay, 0);
    },
    uncollectedCount() {
      return this.rows.reduce((sum, row) => sum + (row.owed.uncollected_count || 0), 0);
    },
    refundedCount() {
      return this.rows.reduce((sum, row) => sum + (row.owed.refunded_count || 0), 0);
    },
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    fetchSummary() {
      const vm = this;
      vm.loading = true;
      axios.get(vm.$apiService.privateLessonPayrollSummaryUrl())
        .then((response) => {
          vm.rows = response.data.coaches;
          vm.generatedAt = response.data.generated_at;
          // A settled coach carrying a negative (refund clawback) balance needs
          // eyes on it, not a collapsed section
          if (vm.rows.some((row) => row.owed.coach_pay < 0)) {
            vm.showSettled = true;
          }
          vm.loading = false;
        })
        .catch((fetchError) => {
          vm.loading = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    dollars(cents) {
      return this.formatAsCurrency(cents);
    },
  },
};
</script>
